import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import { Checkbox, Form, Input } from 'antd';

import { SubTitle } from 'src/components/styled';
import { tenantFormFields } from 'src/utils/constants';
import SelectBox from 'src/components/general-ui/SelectBox';
import Connector, {
  PropsFromRedux,
} from '../containers/SiteGroupModalConnector';
import { PropsFromRedux as PropsFromReduxFromParent } from '../containers/container';
import {
  cityLookupByIsoCountry,
  countryOptions,
  getCountryIso,
  getTimezoneUtcOffsetStr,
  usStatesOptions,
} from 'src/utils/country-city-timezones/utils';
import {
  EachColumn,
  EachRow,
  FormSection,
  StyledForm,
  StyledLoader,
  StyledModal,
  StyledText,
} from './styled';
import { tenantModalModes } from './utils';
import { deepEqual } from 'src/utils/comparators';
import SelectBoxForm, {
  SingleSelectItemOptionType,
} from 'src/components/general-ui/SelectBoxForm';
import { DownOutlined } from '@ant-design/icons';
import { SiteGroupType } from 'src/services/types';
import { CustomerType } from 'src/routes/Customers/modules/types';
import { AddOrEditGroupsProps } from 'src/redux/data/siteGroups/modules/types';
import { criteriaValidator } from 'src/routes/SitesGroups/components/validators';

type CityType = {
  timezone: string;
  city: string;
  iso2: string;
  province: string;
  state_ansi?: string | undefined;
} & SingleSelectItemOptionType;

type Props = {
  isModalVisible: boolean;
  setIsModalVisible: (value: boolean) => void;
  onOk: (tenantData: AddOrEditGroupsProps) => void;
  mode: string;
  intl: IntlShape;
  tenantListRow: SiteGroupType | null;
  currentCustomer: CustomerType | null;
} & Pick<
  PropsFromReduxFromParent,
  | 'modalStatus'
  | 'setModalStatus'
  | 'getOperators'
  | 'operatorsList'
  | 'getCustomers'
  | 'customersList'
> &
  PropsFromRedux;
const SiteGroupModal = ({
  isModalVisible,
  setIsModalVisible,
  onOk,
  mode,
  modalStatus,
  setModalStatus,
  intl,
  tenantListRow,
  currentSiteGroupData,
  getOperators,
  operatorsList,
  currentCustomer,
  getCustomers,
  customersList,
  getAddTenantsParentList,
  sitesList,
  getCurrentSiteGroupData,
  isLoading,
  setCurrentSiteGroups,
}: Props) => {
  const tenantCountryCode = useMemo(
    () => currentSiteGroupData?.address?.countryCode,
    [currentSiteGroupData?.address?.countryCode],
  );
  const tenantCity = useMemo(
    () => currentSiteGroupData?.address?.city,
    [currentSiteGroupData?.address?.city],
  );
  const operatorsCriteria = useMemo(
    () =>
      currentSiteGroupData?.criteria?.find(
        criteria => criteria.criteriaType === 'OPERATOR',
      )?.criteriaValues,
    [currentSiteGroupData?.criteria],
  );
  const statesCriteria = useMemo(
    () =>
      currentSiteGroupData?.criteria?.find(
        criteria => criteria.criteriaType === 'STATE',
      )?.criteriaValues,
    [currentSiteGroupData?.criteria],
  );
  const countriesCriteria = useMemo(
    () =>
      currentSiteGroupData?.criteria?.find(
        criteria => criteria.criteriaType === 'COUNTRY',
      )?.criteriaValues,
    [currentSiteGroupData?.criteria],
  );
  const allSitesCriteria = useMemo(
    () =>
      currentSiteGroupData?.criteria?.find(
        criteria => criteria.criteriaType === 'CUSTOMER',
      )?.criteriaValues,
    [currentSiteGroupData?.criteria],
  );
  const sitesCriteria = useMemo(
    () =>
      currentSiteGroupData?.criteria?.find(
        criteria => criteria.criteriaType === 'SITE',
      )?.criteriaValues,
    [currentSiteGroupData?.criteria],
  );
  const operatorsOptionList = useMemo(
    () =>
      operatorsList?.map(operator => ({
        value: operator.id,
        label: operator.name,
      })),
    [operatorsList],
  );

  const customersOptionList = useMemo(
    () =>
      customersList?.map(customer => ({
        value: customer.id ?? '',
        label: customer.name,
      })),
    [customersList],
  );
  const sitesOptions = useMemo(
    () =>
      sitesList?.map(mtm => ({
        value: mtm.id,
        label: mtm.name,
      })),
    [sitesList],
  );

  const [form] = Form.useForm<AddOrEditGroupsProps>();

  const [selectedCountry, setCountry] = useState<string | undefined | null>(
    tenantCountryCode,
  );
  const [citiesList, setCitiesList] = useState<null | CityType[]>(null);
  const [selectedCity, setCity] = useState<string | null | undefined>(
    tenantCity,
  );

  const modeToPropsMap = useMemo(
    () => ({
      [tenantModalModes.ADD]: {
        title: intl.formatMessage(messages.addTenant),
        okButtonText: intl.formatMessage(messages.add),
        initialValues: {},
      },
      [tenantModalModes.EDIT]: {
        title: intl.formatMessage(messages.editTenant),
        okButtonText: intl.formatMessage(messages.apply),
        initialValues: {},
      },
    }),
    [currentSiteGroupData, intl],
  );

  useEffect(() => {
    setCountry(currentSiteGroupData?.address?.countryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCountryCode]);

  useEffect(() => {
    const citiesByCountry = cityLookupByIsoCountry(
      getCountryIso(selectedCountry),
    );

    citiesByCountry &&
      setCitiesList(
        citiesByCountry.map(city => ({
          ...city,
          value: city.id,
          label: city.name,
        })),
      );
  }, [selectedCountry]);

  useEffect(() => {
    setCity(currentSiteGroupData?.address?.city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCity]);

  useEffect(() => {
    if (mode === tenantModalModes.ADD) {
      form.resetFields();
      setCountry(null);
      setCitiesList(null);
      setCity(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (!currentCustomer?.id) {
      getCustomers();
      return;
    }
    form.setFieldsValue({ customerId: currentCustomer?.id });
  }, [currentCustomer?.id, getCustomers]);

  useEffect(() => {
    if (!tenantListRow?.id) {
      return;
    }
    getCurrentSiteGroupData({ id: tenantListRow.id });
  }, [tenantListRow?.id, getCurrentSiteGroupData]);

  // When customer field changes
  useEffect(() => {
    if (!form.getFieldValue(tenantFormFields.CUSTOMER_ID)) {
      return;
    }
    getOperators({
      customerId: form.getFieldValue(tenantFormFields.CUSTOMER_ID) as string,
      limit: 1000000,
    });
    getAddTenantsParentList({
      customerID: form.getFieldValue(tenantFormFields.CUSTOMER_ID) as string,
    });
    if (mode === tenantModalModes.EDIT) {
      return;
    }
    form.setFieldValue(tenantFormFields.OPERATOR_ID, null);
    form.setFieldValue(tenantFormFields.STATE, null);
    form.setFieldValue(tenantFormFields.SITES, null);
  }, [
    getOperators,
    getAddTenantsParentList,
    form.getFieldValue(tenantFormFields.CUSTOMER_ID),
  ]);

  // When all sites checkbox is checked
  useEffect(() => {
    if (!form.getFieldValue(tenantFormFields.ALL_SITES)) {
      return;
    }
    form.setFieldValue(tenantFormFields.OPERATOR_ID, null);
    form.setFieldValue(tenantFormFields.STATE, null);
    form.setFieldValue(tenantFormFields.SITES, null);
  }, [getOperators, form.getFieldValue(tenantFormFields.ALL_SITES)]);

  const onCitySelect = (cityOption: {
    timezone: string;
    city: string;
    iso2: string;
    province: string;
    state_ansi?: string;
  }) => {
    const { timezone, city, iso2, state_ansi, province } = cityOption;
    const utcOffsetStr = getTimezoneUtcOffsetStr(timezone);

    form.setFieldsValue({
      stateAddress: state_ansi ?? province,
      city: city,
      countryCode: iso2,
      timeZoneOffset: utcOffsetStr,
      timeZoneId: timezone,
    });
  };

  return (
    <StyledModal
      width={982}
      title={modeToPropsMap?.[mode]?.title}
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      okButtonText={modeToPropsMap?.[mode]?.okButtonText}
      onOk={() => {
        form.submit();
      }}
      destroyOnClose
      modalStatus={modalStatus}
      setModalStatus={setModalStatus}
      onClose={() =>
        setCurrentSiteGroups({
          data: null,
        })
      }
    >
      {isLoading || (tenantListRow?.id && !currentSiteGroupData) ? (
        <StyledLoader />
      ) : (
        <StyledForm
          form={form}
          layout="vertical"
          // @ts-ignore TODO: Fix typing later
          onFinish={(values: AddOrEditGroupsProps) => {
            onOk(values);
          }}
          validateMessages={tenantFormValidateMessages}
        >
          {() => {
            return (
              <>
                <SubTitle>{intl.formatMessage(messages.general)}</SubTitle>
                <FormSection>
                  <EachColumn>
                    <Form.Item
                      name={tenantFormFields.NAME}
                      label={intl.formatMessage(messages.nameFieldLabel)}
                      rules={[{ required: true }]}
                      initialValue={currentSiteGroupData?.name}
                    >
                      <Input />
                    </Form.Item>
                  </EachColumn>
                  <EachColumn>
                    <Form.Item
                      name={tenantFormFields.CUSTOMER_ID}
                      label={intl.formatMessage(messages.customer)}
                      dependencies={[tenantFormFields.OPERATOR_ID]}
                      rules={[{ required: true }]}
                      initialValue={
                        !currentCustomer?.id
                          ? currentSiteGroupData?.customerId
                          : currentCustomer?.id
                      }
                    >
                      {!currentCustomer?.id && customersOptionList && (
                        <SelectBoxForm
                          onClear={() =>
                            form.setFieldsValue({
                              [tenantFormFields.CUSTOMER_ID]: null,
                            })
                          }
                          disabled={mode === tenantModalModes.EDIT}
                          options={customersOptionList}
                          placeholder={intl.formatMessage(messages.customer)}
                        />
                      )}
                      {currentCustomer?.id && (
                        <SelectBoxForm
                          disabled
                          options={[
                            {
                              value: currentCustomer.id,
                              label: currentCustomer.name,
                            },
                          ]}
                          placeholder={intl.formatMessage(messages.customer)}
                        />
                      )}
                    </Form.Item>
                  </EachColumn>
                </FormSection>
                <SubTitle>
                  {intl.formatMessage(messages.groupCriteria)}
                </SubTitle>
                <FormSection>
                  <EachColumn>
                    <EachRow>
                      <Form.Item
                        className={'full-width'}
                        name={tenantFormFields.OPERATOR_ID}
                        label={intl.formatMessage(messages.operator)}
                        initialValue={operatorsCriteria ?? []}
                        rules={criteriaValidator()}
                        dependencies={[
                          tenantFormFields.CUSTOMER_ID,
                          tenantFormFields.STATE,
                          tenantFormFields.COUNTRY_CRITERIA,
                          tenantFormFields.SITES,
                          tenantFormFields.ALL_SITES,
                        ]}
                      >
                        {
                          <SelectBoxForm
                            mode={'multiple'}
                            suffixIcon={<DownOutlined />}
                            disabled={
                              !form.getFieldValue(
                                tenantFormFields.CUSTOMER_ID,
                              ) ||
                              !!form.getFieldValue(tenantFormFields.ALL_SITES)
                            }
                            options={operatorsOptionList ?? []}
                            placeholder={intl.formatMessage(messages.operator)}
                          />
                        }
                      </Form.Item>
                      <StyledText>And</StyledText>
                    </EachRow>
                    <Form.Item
                      className={'full-width'}
                      name={tenantFormFields.STATE}
                      label={intl.formatMessage(messages.statesFieldLabel)}
                      initialValue={statesCriteria}
                      rules={criteriaValidator()}
                      dependencies={[
                        tenantFormFields.OPERATOR_ID,
                        tenantFormFields.COUNTRY_CRITERIA,
                        tenantFormFields.SITES,
                        tenantFormFields.ALL_SITES,
                      ]}
                    >
                      <SelectBoxForm
                        mode={'multiple'}
                        disabled={
                          !form.getFieldValue(tenantFormFields.CUSTOMER_ID) ||
                          !!(
                            form.getFieldValue(
                              tenantFormFields.COUNTRY_CRITERIA,
                            ) as []
                          )?.length ||
                          !!form.getFieldValue(tenantFormFields.ALL_SITES)
                        }
                        options={[
                          ...(usStatesOptions as SingleSelectItemOptionType[]),
                        ]}
                        placeholder={intl.formatMessage(
                          messages.statesFieldLabel,
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      className={'full-width'}
                      name={tenantFormFields.COUNTRY_CRITERIA}
                      label={intl.formatMessage(messages.countriesFieldLabel)}
                      initialValue={countriesCriteria}
                      rules={criteriaValidator()}
                      dependencies={[
                        tenantFormFields.OPERATOR_ID,
                        tenantFormFields.STATE,
                        tenantFormFields.SITES,
                        tenantFormFields.ALL_SITES,
                      ]}
                    >
                      <SelectBoxForm
                        mode={'multiple'}
                        disabled={
                          !form.getFieldValue(tenantFormFields.CUSTOMER_ID) ||
                          !!(
                            form.getFieldValue(
                              tenantFormFields.STATE,
                            ) as string[]
                          )?.length ||
                          !!form.getFieldValue(tenantFormFields.ALL_SITES)
                        }
                        options={[
                          ...(countryOptions as SingleSelectItemOptionType[]),
                        ]}
                        placeholder={intl.formatMessage(
                          messages.countriesFieldLabel,
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      className={'full-width'}
                      name={tenantFormFields.SITES}
                      label={intl.formatMessage(messages.sitesLabel)}
                      initialValue={sitesCriteria}
                      rules={criteriaValidator()}
                      dependencies={[
                        tenantFormFields.OPERATOR_ID,
                        tenantFormFields.COUNTRY_CRITERIA,
                        tenantFormFields.STATE,
                        tenantFormFields.ALL_SITES,
                      ]}
                    >
                      <SelectBoxForm
                        mode={'multiple'}
                        disabled={
                          !form.getFieldValue(tenantFormFields.CUSTOMER_ID) ||
                          !!form.getFieldValue(tenantFormFields.ALL_SITES)
                        }
                        options={sitesOptions}
                        placeholder={intl.formatMessage(messages.sitesLabel)}
                      />
                    </Form.Item>
                    <Form.Item
                      className={'checkbox-field'}
                      name={tenantFormFields.ALL_SITES}
                      label={intl.formatMessage(messages.allSites)}
                      valuePropName="checked"
                      initialValue={!!allSitesCriteria}
                      rules={criteriaValidator()}
                      dependencies={[
                        tenantFormFields.OPERATOR_ID,
                        tenantFormFields.COUNTRY_CRITERIA,
                        tenantFormFields.STATE,
                        tenantFormFields.SITES,
                      ]}
                    >
                      <Checkbox />
                    </Form.Item>
                  </EachColumn>
                </FormSection>
                <SubTitle>
                  {intl.formatMessage(messages.timezoneConfigurationsLabel)}
                </SubTitle>
                <FormSection>
                  <EachColumn>
                    <Form.Item
                      name={tenantFormFields.COUNTRY_CODE}
                      label={intl.formatMessage(messages.countryFieldLabel)}
                      rules={[{ required: true }]}
                      initialValue={selectedCountry}
                    >
                      <SelectBox
                        handleChange={value => {
                          form.setFieldsValue({
                            city: null,
                            countryCode: value,
                          });
                          setCountry(value);
                          setCity(null);
                        }}
                        onClear={() => {
                          form.setFieldsValue({
                            countryCode: null,
                            city: null,
                          });
                          setCountry(null);
                          setCity(null);
                        }}
                        selectedItem={selectedCountry ?? undefined}
                        options={countryOptions}
                        placeholder={intl.formatMessage(messages.searchCountry)}
                      />
                    </Form.Item>
                    <Form.Item
                      name={tenantFormFields.ADDRESS1}
                      label={intl.formatMessage(messages.address1FieldLabel)}
                      initialValue={currentSiteGroupData?.address?.address1}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      hidden={true}
                      name={tenantFormFields.STATE_ADDRESS}
                      label={intl.formatMessage(messages.address1FieldLabel)}
                      initialValue={currentSiteGroupData?.address?.state}
                    >
                      <Input />
                    </Form.Item>
                  </EachColumn>
                  <EachColumn>
                    <Form.Item
                      name={tenantFormFields.CITY}
                      label={intl.formatMessage(messages.cityFieldLabel)}
                      rules={[{ required: true }]}
                      initialValue={selectedCity}
                    >
                      <SelectBox
                        // @ts-ignore TODO check this later
                        handleChange={(_value, cityObj: CityType) => {
                          onCitySelect(cityObj);
                        }}
                        selectedItem={selectedCity ?? undefined}
                        disabled={!selectedCountry}
                        options={
                          selectedCountry && citiesList ? citiesList : []
                        }
                        placeholder={intl.formatMessage(messages.searchCity)}
                        notFoundContent={intl.formatMessage(
                          messages.notFoundCity,
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      className={'timezone-id'}
                      label={intl.formatMessage(messages.timezoneLabel)}
                      name={tenantFormFields.TIMEZONE_ID}
                      initialValue={currentSiteGroupData?.timeZoneId}
                    >
                      <Input readOnly />
                    </Form.Item>
                    <Form.Item
                      className={'timezone-offset'}
                      label={intl.formatMessage(messages.timezoneOffsetLabel)}
                      name={tenantFormFields.TIMEZONE_UTCOFFSET_STR}
                      initialValue={currentSiteGroupData?.timeZoneOffset}
                    >
                      <Input bordered={false} readOnly />
                    </Form.Item>
                  </EachColumn>
                </FormSection>
              </>
            );
          }}
        </StyledForm>
      )}
    </StyledModal>
  );
};

const messages = defineMessages({
  general: {
    id: 'components.SiteGroupModal.general',
    defaultMessage: 'Site Group details',
  },
  groupCriteria: {
    id: 'components.SiteGroupModal.groupCriteria',
    defaultMessage: 'Group criteria',
  },
  addTenant: {
    id: 'components.SiteGroupModal.addTenant',
    defaultMessage: 'New Site Group',
  },
  editTenant: {
    id: 'components.SiteGroupModal.editTenant',
    defaultMessage: 'Edit Site Group',
  },
  apply: {
    id: 'components.SiteGroupModal.apply',
    defaultMessage: 'Apply',
  },
  add: {
    id: 'components.SiteGroupModal.add',
    defaultMessage: 'Add',
  },
  searchCountry: {
    id: 'components.SiteGroupModal.searchCountry',
    defaultMessage: 'Search country...',
  },
  notFoundCity: {
    id: 'components.SiteGroupModal.notFoundCity',
    defaultMessage: 'Please choose a city close to your area.',
  },
  searchCity: {
    id: 'components.SiteGroupModal.searchCity',
    defaultMessage: 'Search city...',
  },
  nameFieldLabel: {
    id: 'components.SiteGroupModal.nameFieldLabel',
    defaultMessage: 'Group Name',
  },
  countryFieldLabel: {
    id: 'components.SiteGroupModal.countryFieldLabel',
    defaultMessage: 'Country',
  },
  statesFieldLabel: {
    id: 'components.SiteGroupModal.statesFieldLabel',
    defaultMessage: 'States',
  },
  countriesFieldLabel: {
    id: 'components.SiteGroupModal.countriesFieldLabel',
    defaultMessage: 'Countries',
  },
  sitesLabel: {
    id: 'components.SiteGroupModal.sitesLabel',
    defaultMessage: 'Sites',
  },
  cityFieldLabel: {
    id: 'components.SiteGroupModal.cityFieldLabel',
    defaultMessage: 'City',
  },
  address1FieldLabel: {
    id: 'components.SiteGroupModal.address1FieldLabel',
    defaultMessage: 'Address1',
  },
  timezoneConfigurationsLabel: {
    id: 'components.SiteGroupModal.timezoneConfigurationsLabel',
    defaultMessage: 'Timezone configurations',
  },
  timezoneLabel: {
    id: 'components.SiteGroupModal.timezoneLabel',
    defaultMessage: 'Timezone',
  },
  timezoneOffsetLabel: {
    id: 'components.SiteGroupModal.timezoneOffsetLabel',
    defaultMessage: 'UTC',
  },
  operator: {
    id: 'components.SiteGroupModal.operator',
    defaultMessage: 'Operator',
  },
  customer: {
    id: 'components.SiteGroupModal.customer',
    defaultMessage: 'Customer',
  },
  allSites: {
    id: 'components.SiteGroupModal.allSites',
    defaultMessage: 'All Sites',
  },
});

export const tenantFormValidateMessages = {
  required: '${label} is required!',
  pattern: { mismatch: '${label} is not a valid ${label}' },
};

export default Connector(
  injectIntl(
    React.memo(SiteGroupModal, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
